/**
 * The timing readout's arithmetic: what a run of fingering times comes to, and
 * how fine the clock they were read from is.
 */
import type { Clock } from './phraseFingering';

/** What the readout shows of the times measured so far, in milliseconds. */
export interface TimingSummary {
  count: number;
  last: number;
  mean: number;
  median: number;
  p95: number;
  p99: number;
}

/** The smallest sample that at least `percent` per cent of the samples do not exceed. */
const percentileOf = (ascending: ReadonlyArray<number>, percent: number): number =>
  ascending[Math.max(0, Math.ceil((percent / 100) * ascending.length) - 1)];

/** The summary of the samples in the order they were taken; null before the first. */
export const timingSummaryOf = (samples: ReadonlyArray<number>): TimingSummary | null => {
  if (samples.length === 0) return null;
  const ascending = [...samples].sort((a, b) => a - b);
  return {
    count: samples.length,
    last: samples[samples.length - 1],
    mean: samples.reduce((sum, sample) => sum + sample, 0) / samples.length,
    median: percentileOf(ascending, 50),
    p95: percentileOf(ascending, 95),
    p99: percentileOf(ascending, 99),
  };
};

const CLOCK_STEPS_WATCHED = 5;
const CLOCK_READS_AT_MOST = 1000000;

/**
 * The smallest step the clock is seen to make, in milliseconds; null when it
 * never moved. Browsers round their clocks, some to a whole millisecond: a time
 * shorter than a step reads as nothing or as one step, so the percentiles are
 * only as fine as this and the mean is the figure to trust below it.
 *
 * Reads the clock until it has stepped a few times, which takes that many steps
 * of real time, so call it once and not while a phrase is being fingered.
 */
export const clockStepOf = (
  clock: Clock,
  stepsWatched: number = CLOCK_STEPS_WATCHED,
  readsAtMost: number = CLOCK_READS_AT_MOST,
): number | null => {
  let smallestStep: number | null = null;
  let stepsSeen = 0;
  let previous = clock();
  for (let read = 0; read < readsAtMost && stepsSeen < stepsWatched; read += 1) {
    const reading = clock();
    if (reading > previous) {
      const step = reading - previous;
      smallestStep = smallestStep === null ? step : Math.min(smallestStep, step);
      stepsSeen += 1;
    }
    previous = reading;
  }
  return smallestStep;
};
